import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import '../styles/Donate.css';

const QUICK_AMOUNTS = [1000, 10000, 50000, 100000];

export default function DonatePage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(2500);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);

  const amount = useCustom ? parseFloat(customAmount) || 0 : selected;

  const handleQuickSelect = (value) => {
    setUseCustom(false);
    setSelected(value);
    setPaymentUrl(null);
  };

  const handleCustomChange = (e) => {
    setUseCustom(true);
    setCustomAmount(e.target.value);
    setPaymentUrl(null);
  };

  const handleDonate = async () => {
    setError(null);
    if (!amount || amount < 100) {
      setError('Minimum donation is ₦100');
      return;
    }
    try {
      setLoading(true);
      const response = await API.post('/api/billing/donate', { amount });
      if (!response.data.payment_url) {
        setError('No payment URL returned. Please try again.');
        return;
      }
      // We don't navigate here with JS (window.location / window.open).
      // Installed home-screen apps on Android (WebAPKs) often trap
      // JS-driven navigation inside their own restricted container instead
      // of handing off to the real browser, which is what caused the blank
      // page. Rendering a real <a> link below and letting the user tap it
      // is handled natively by the OS and reliably opens the real browser.
      setPaymentUrl(response.data.payment_url);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="donate-page">
      <header className="donate-header">
        <button className="donate-back" onClick={() => navigate(-1)} aria-label="Back">
          ←
        </button>
        <h1>Donate</h1>
        <div className="donate-spacer" />
      </header>

      <div className="donate-hero">
        <span className="donate-hero-icon">❤️</span>
        <h2>Support Godly Match</h2>
        <p>
          Your gift helps us onboard more members and keep building a safe, faith-centered space for
          Christian singles to meet. Every donation counts, whatever the size.
        </p>
      </div>

      {error && <div className="donate-error">{error}</div>}

      <div className="donate-amounts">
        {QUICK_AMOUNTS.map((value) => (
          <button
            key={value}
            className={`donate-amount-chip ${!useCustom && selected === value ? 'selected' : ''}`}
            onClick={() => handleQuickSelect(value)}
          >
            ₦{value.toLocaleString()}
          </button>
        ))}
      </div>

      <div className="donate-custom">
        <label htmlFor="custom-amount">Or enter any amount</label>
        <div className="donate-custom-input">
          <span className="donate-currency">₦</span>
          <input
            id="custom-amount"
            type="number"
            min="100"
            step="1"
            placeholder="Enter amount"
            value={customAmount}
            onChange={handleCustomChange}
            onFocus={() => setUseCustom(true)}
          />
        </div>
      </div>

      {!paymentUrl ? (
        <button
          className="donate-submit"
          onClick={handleDonate}
          disabled={loading || !amount || amount < 100}
        >
          {loading ? 'Preparing checkout...' : `Donate ₦${amount ? amount.toLocaleString() : '0'}`}
        </button>
      ) : (
        <a
          className="donate-submit"
          href={paymentUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
        >
          Tap here to complete your ₦{amount.toLocaleString()} donation
        </a>
      )}

      <p className="donate-note">
        Payments are securely processed by Flutterwave. Donations are not
        tax-deductible and do not grant Godly+ or Platinum features.
      </p>
    </div>
  );
}